import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assetsAPI, companiesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { formatDate, getStatusColor, getStatusLabel, getCategoryColor, getCategoryLabel, cn } from '../lib/utils';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, CalendarIcon, AlertTriangle, Trash2 } from 'lucide-react';

export function AssetForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { isAdmin } = useAuth();
    const isEdit = !!id;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [asset, setAsset] = useState(null);
    const [error, setError] = useState('');
    const [installDate, setInstallDate] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        serial_number: '',
        category: 'network',
        company_id: '',
        installation_date: '',
        warranty_months: 24,
        description: '',
        barcode: ''
    });

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const [companiesRes] = await Promise.all([companiesAPI.getAll()]);
            setCompanies(companiesRes.data);

            if (isEdit) {
                const assetRes = await assetsAPI.getOne(id);
                const assetData = assetRes.data;
                setAsset(assetData);
                setFormData({
                    name: assetData.name,
                    serial_number: assetData.serial_number,
                    category: assetData.category,
                    company_id: assetData.company_id,
                    installation_date: assetData.installation_date,
                    warranty_months: assetData.warranty_months,
                    description: assetData.description || '',
                    barcode: assetData.barcode || ''
                });
                setInstallDate(new Date(assetData.installation_date));
            }
        } catch (err) {
            console.error(err);
            setError('Veri yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleDateSelect = (date) => {
        setInstallDate(date);
        if (date) {
            setFormData({ ...formData, installation_date: date.toISOString() });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);

        try {
            if (isEdit) {
                await assetsAPI.update(id, formData);
                toast.success('Varlık güncellendi');
            } else {
                await assetsAPI.create(formData);
                toast.success('Varlık oluşturuldu');
            }
            navigate('/assets');
        } catch (err) {
            setError(err.response?.data?.detail || 'İşlem başarısız oldu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Bu varlığı silmek istediğinizden emin misiniz?')) return;
        
        setDeleting(true);
        try {
            await assetsAPI.delete(id);
            toast.success('Varlık silindi');
            navigate('/assets');
        } catch (err) {
            toast.error('Silme işlemi başarısız oldu');
        } finally {
            setDeleting(false);
        }
    };

    if (!isAdmin && !isEdit) {
        navigate('/assets');
        return null;
    }

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto space-y-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="asset-form-page">
            <Button
                variant="ghost"
                onClick={() => navigate('/assets')}
                className="mb-4"
                data-testid="back-btn"
            >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
            </Button>

            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div>
                            <CardTitle className="font-heading text-xl">
                                {isEdit ? 'Varlık Detayı' : 'Yeni Varlık'}
                            </CardTitle>
                            <CardDescription>
                                {isEdit ? 'Varlık bilgilerini görüntüleyin ve düzenleyin' : 'Yeni cihaz veya yazılım ekleyin'}
                            </CardDescription>
                        </div>
                        {isEdit && asset && (
                            <div className="flex gap-2">
                                <Badge className={getStatusColor(asset.status)}>
                                    {getStatusLabel(asset.status)}
                                </Badge>
                                <Badge variant="outline" className={getCategoryColor(asset.category)}>
                                    {getCategoryLabel(asset.category)}
                                </Badge>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <Alert variant="destructive">
                                <AlertTriangle className="w-4 h-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="name">Varlık Adı *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Örn: Cisco Switch 24 Port"
                                    required
                                    disabled={!isAdmin}
                                    data-testid="name-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="serial_number">Seri Numarası *</Label>
                                <Input
                                    id="serial_number"
                                    value={formData.serial_number}
                                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                                    placeholder="Örn: SN123456789"
                                    required
                                    disabled={!isAdmin}
                                    data-testid="serial-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="category">Kategori *</Label>
                                <Select 
                                    value={formData.category} 
                                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                                    disabled={!isAdmin}
                                >
                                    <SelectTrigger data-testid="category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="network">Ağ Cihazı</SelectItem>
                                        <SelectItem value="software">Yazılım Lisansı</SelectItem>
                                        <SelectItem value="low_voltage">Zayıf Akım</SelectItem>
                                        <SelectItem value="other">Diğer</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="company">Şirket *</Label>
                                <Select 
                                    value={formData.company_id} 
                                    onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                                    disabled={!isAdmin}
                                >
                                    <SelectTrigger data-testid="company-select">
                                        <SelectValue placeholder="Şirket seçin" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map(company => (
                                            <SelectItem key={company.id} value={company.id}>
                                                {company.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label>Kurulum Tarihi *</Label>
                                <Popover>
                                    <PopoverTrigger asChild disabled={!isAdmin}>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal",
                                                !installDate && "text-muted-foreground"
                                            )}
                                            data-testid="date-picker"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {installDate ? format(installDate, "PPP", { locale: tr }) : "Tarih seçin"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={installDate}
                                            onSelect={handleDateSelect}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="warranty_months">Garanti Süresi (Ay) *</Label>
                                <Input
                                    id="warranty_months"
                                    type="number"
                                    min={0}
                                    value={formData.warranty_months}
                                    onChange={(e) => setFormData({ ...formData, warranty_months: parseInt(e.target.value) || 0 })}
                                    disabled={!isAdmin}
                                    data-testid="warranty-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="barcode">Barkod (Opsiyonel)</Label>
                            <Input
                                id="barcode"
                                value={formData.barcode}
                                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                placeholder="Barkod numarası"
                                disabled={!isAdmin}
                                data-testid="barcode-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Açıklama (Opsiyonel)</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Varlık hakkında ek bilgiler"
                                rows={3}
                                disabled={!isAdmin}
                                data-testid="description-input"
                            />
                        </div>

                        {isEdit && asset && (
                            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Garanti Bitiş:</span>
                                    <span className={`font-medium ${asset.status === 'warranty_expired' ? 'text-red-600' : asset.status === 'warranty_expiring' ? 'text-amber-600' : 'text-slate-800'}`}>
                                        {formatDate(asset.warranty_end_date)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Oluşturma:</span>
                                    <span className="text-slate-800">{formatDate(asset.created_at)}</span>
                                </div>
                            </div>
                        )}

                        {isAdmin && (
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="submit"
                                    className="bg-orwion-green hover:bg-orwion-green-hover"
                                    disabled={saving}
                                    data-testid="submit-btn"
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    {isEdit ? 'Güncelle' : 'Oluştur'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/assets')}
                                    data-testid="cancel-btn"
                                >
                                    İptal
                                </Button>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        className="ml-auto"
                                        data-testid="delete-btn"
                                    >
                                        {deleting ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4 mr-2" />
                                        )}
                                        Sil
                                    </Button>
                                )}
                            </div>
                        )}
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default AssetForm;
